import React, { useEffect, useState } from "react";
import PageWithNavbar from "@/components/PageWithNavbar";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  TransparentCard,
} from "../components/ui/card";
import { CheckCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { authAPI, ResetPasswordRequest } from "../services/auth";
import { ApiError } from "@/services/apiClient";

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();

  const [userId, setUserId] = useState<string>("");
  const [token, setToken] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");

  const [errors, setErrors] = useState<{
    general?: string;
    password?: string;
    confirmPassword?: string;
  }>({});

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const uid = params.get("uid") || params.get("userId") || "";
    setUserId(uid);
    const t = params.get("token") || params.get("t") || "";
    setToken(t);
  }, []);

  const validate = (): boolean => {
    const newErrors: typeof errors = {};

    if (!token) {
      newErrors.general = "Invalid or missing reset token. Please request a new reset link.";
    }

    if (!password || password.length < 8) {
      newErrors.password = "Password must be at least 8 characters long";
    }

    if (password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setIsLoading(true);
    setErrors({});

    try {
      const payload: ResetPasswordRequest = {
        userId: parseInt(userId),
        token,
        newPassword: password
      };

      await authAPI.resetPassword(payload);

      setIsSubmitted(true);

      // Redirect to login after a short delay so user sees success state
      setTimeout(() => navigate("/login?passwordReset=success"), 1500);
    } catch (err: any) {
      const message = err instanceof ApiError 
        ? err.message 
        : "Failed to reset password. Please try again.";
      setErrors({ general: message });
      console.error("Reset password error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageWithNavbar>
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-md mx-auto">
            {!isSubmitted ? (
              <>
                <div className="text-center mb-8">
                  <div className="flex items-center justify-center mb-4">
                    <h1 className="text-3xl font-bold">Reset Password</h1>
                  </div>
                </div>

                <TransparentCard>
                  <CardHeader>
                    <CardTitle>Choose a New Password</CardTitle>
                    <CardDescription>
                      Create a new secure password (at least 8 characters)
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                      {errors.general && (
                        <div className="p-3 text-sm text-red-800 bg-red-100 border border-red-300 rounded-md">
                          {errors.general}
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium mb-2">New Password</label>
                        <Input
                          type="password"
                          placeholder="Enter new password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          disabled={isLoading}
                        />
                        {errors.password && (
                          <p className="text-red-500 text-xs mt-1">{errors.password}</p>
                        )}
                        <p className="text-xs text-muted-foreground mt-2">Must be at least 8 characters long</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Confirm Password</label>
                        <Input
                          type="password"
                          placeholder="Confirm new password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          disabled={isLoading}
                        />
                        {errors.confirmPassword && (
                          <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>
                        )}
                      </div>

                      <Button type="submit" className="w-full" disabled={isLoading}>
                        {isLoading ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                            Resetting...
                          </>
                        ) : (
                          "Reset Password"
                        )}
                      </Button>

                      <div className="mt-4 text-sm text-muted-foreground text-center">
                        <Button variant="link" className="p-0 h-auto" onClick={() => navigate("/login")}>Back to sign in</Button>
                      </div>
                    </form>
                  </CardContent>
                </TransparentCard>
              </>
            ) : (
              <>
                <div className="text-center mb-8">
                  <div className="flex items-center justify-center mb-4">
                    <CheckCircle className="h-12 w-12 text-green-500 mr-3" />
                    <h1 className="text-3xl font-bold">Password Reset</h1>
                  </div>
                  <p className="text-muted-foreground">Your password has been successfully reset.</p>
                </div>

                <TransparentCard>
                  <CardContent>
                    <div className="text-center">
                      <Button onClick={() => navigate("/login")} className="w-full">Sign in</Button>
                    </div>
                  </CardContent>
                </TransparentCard>
              </>
            )}
          </div>
        </div>
      </div>
    </PageWithNavbar>
  );
};

export default ResetPasswordPage;
